import { useTheme } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { Section } from 'src/components/section';
import { FieldTypes } from 'src/components/controlled-field/types';
import { ControlledField } from 'src/components/controlled-field';
import { Button } from 'src/components/button';
import { getTheme } from 'src/utils/getTheme';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning-hos.svg';
import { usePasswordLoginSupabase } from 'src/auth/hooks/use-password-login-supabase';
import type { LoginFormProps } from 'src/features/login-form/types';

import { loginDataKeys } from '../constants';
import { ForgottenPasswordForm } from '../forgotten-password-form';

import { styles } from './styles';

export const PasswordLoginForm: FC<LoginFormProps> = ({ toggleLoginMode, onClose }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [showPasswordReset, setShowPasswordReset] = useState(false);

    const { mutate, error, isPending, isSuccess } = usePasswordLoginSupabase();

    const { isHosTheme } = getTheme();
    const { control, handleSubmit, watch, register, unregister } = useFormContext();

    const watchEmailField = watch('email');
    const watchPasswordField = watch('password');

    const onSubmit = handleSubmit(({ email, password }) => {
        mutate({ email, password });
    });

    const handleSubmitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && watchEmailField && watchPasswordField) {
            event.preventDefault();
            onSubmit();
        }
    };

    if (isSuccess) {
        onClose();
        unregister(loginDataKeys.password);
        return null;
    }

    if (showPasswordReset) {
        return (
            <ForgottenPasswordForm
                onClose={onClose}
                hidePasswordReset={() => setShowPasswordReset(false)}
            />
        );
    }

    return (
        <Box css={styles.loginDialogWrapper}>
            <Section header={t('login_dialog_title')}>
                {error && (
                    <Box css={styles.loginDialogErrorBox}>
                        {isHosTheme && <WarningIcon />}
                        <Typography color={theme.palette.primary.main} variant="subtitle1">
                            {error.message}
                        </Typography>
                    </Box>
                )}

                <Stack spacing="20px" css={styles.loginFormWrapper} onKeyDown={handleSubmitOnEnter}>
                    <ControlledField
                        type="email"
                        {...register(loginDataKeys.email)}
                        name={loginDataKeys.email}
                        control={control}
                        label={t('login_dialog_email_address')}
                        placeholder={t('login_dialog_email_address')}
                        id="login-email"
                    />
                    <ControlledField
                        fieldType={FieldTypes.Password}
                        {...register(loginDataKeys.password)}
                        name={loginDataKeys.password}
                        control={control}
                        label={t('login_dialog_password')}
                        placeholder={t('login_dialog_password')}
                        id="login-password"
                    />
                </Stack>
                <Button
                    disableElevation
                    css={styles.loginButton}
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!watchEmailField || !watchPasswordField}
                    loading={isPending}
                    id="login-email-password-submit"
                >
                    {t('login_dialog_cta')}
                </Button>

                <Box css={styles.linkWrapper}>
                    <Typography
                        css={styles.forgottenPasswordText}
                        sx={{ cursor: 'pointer', display: 'block', my: 4 }}
                        onClick={toggleLoginMode}
                        id="login-switch-otp"
                    >
                        {t('login_dialog_sign_in_with_otp')}
                    </Typography>

                    <hr css={styles.separationLine} />

                    {!showPasswordReset && (
                        <Typography
                            css={styles.forgottenPasswordText}
                            variant={isHosTheme ? 'subtitle1' : 'subtitle2'}
                            sx={{ display: 'block', cursor: 'pointer' }}
                            onClick={() => setShowPasswordReset(true)}
                            id="login-password-reset"
                        >
                            {t('login_dialog_forgotten_password')}
                        </Typography>
                    )}
                </Box>
            </Section>
        </Box>
    );
};
